import React, { useEffect, useState } from "react";
import { db, auth } from "../firebase";
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { getOrInitStock, calculateClosing } from "../utils/stockUtils";
import { onAuthStateChanged } from "firebase/auth";
import Layout from "../components/Layout";
import { useNavigate } from "react-router-dom";
import { getISTDate } from "../utils/dateUtils";

function DistributorDashboard() {
  const [userProfile, setUserProfile] = useState(null);
  const [dsList, setDsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalStock, setTotalStock] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate("/");
        return;
      }
      const snap = await getDoc(doc(db, "users", user.uid));
      if (snap.exists()) {
        const profile = snap.data();
        if (profile.role !== "distributor" && profile.role !== "superadmin") {
          navigate("/");
          return;
        }
        setUserProfile(profile);
        loadDistributorData(profile);
      }
    });
    return () => unsub();
  }, [navigate]);

  const loadDistributorData = async (profile) => {
    // Trigger Background Stock Sync for Distributor on load
    getOrInitStock("distributor", profile.partyId, getISTDate(), profile.clientId || "pan").catch(console.error);

    setLoading(true);
    try {
      // 1. Load DS List for this distributor
      let dsQuery;
      if (profile.role === "superadmin") {
        dsQuery = query(collection(db, "dsMaster"));
      } else {
        dsQuery = query(
          collection(db, "dsMaster"), 
          where("clientId", "==", profile.clientId),
          where("distributorId", "==", profile.partyId)
        );
      }
      const dsSnap = await getDocs(dsQuery);
      setDsList(dsSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // 2. Load Inventory (Today's Summary)
      const todayStr = getISTDate();
      const stockRef = doc(db, "distributor_stock_daily", `${profile.partyId}_${todayStr}`);
      const stockSnap = await getDoc(stockRef);
      
      let tStock = 0;
      if (stockSnap.exists()) {
          const inv = stockSnap.data().inventory || {};
          Object.values(inv).forEach(item => {
              tStock += (item.opening || 0) + (item.received || 0) - (item.sold || 0);
          });
      }
      setTotalStock(tStock);
      
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Layout><div style={{ padding: 20 }}>Loading Distributor Dashboard...</div></Layout>;

  return (
    <Layout>
      <div className="layout-content">
        {/* Header Section */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 48 }}>
            <div>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                    <span className="badge badge-success" style={{ background: "rgba(16, 185, 129, 0.1)", color: "var(--success)", padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 800 }}>LIVE NETWORK</span>
                    <span style={{ fontSize: 13, color: "var(--text-muted)", fontWeight: 500 }}>System Integrity: 99.9%</span>
                </div>
                <h1 style={{ fontSize: 36, margin: 0, fontWeight: 900, letterSpacing: "-0.03em" }}>Operations Command Center</h1>
                <p style={{ color: "var(--text-muted)", fontSize: 16, marginTop: 4 }}>Real-time oversight of distributor network <strong>{userProfile?.partyId}</strong></p>
            </div>
            <div style={{ display: "flex", gap: 16 }}>
                <button 
                    onClick={() => navigate("/distributor-mobile")}
                    className="btn-secondary"
                    style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 32px", fontSize: 16, borderRadius: 12, border: "2px solid var(--primary)", color: "var(--primary)" }}
                >
                    <span style={{ fontSize: 24, lineHeight: 1 }}>📱</span> 
                    <span style={{ fontWeight: 800, letterSpacing: "0.02em" }}>MOBILE VIEW</span>
                </button>
                <button 
                    onClick={() => navigate("/distributor-bill-ds")}
                    className="btn-primary"
                    style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 32px", fontSize: 16, borderRadius: 12, boxShadow: "0 10px 25px -5px rgba(255, 107, 0, 0.4)" }}
                >
                    <span style={{ fontSize: 24, lineHeight: 1 }}>+</span> 
                    <span style={{ fontWeight: 800, letterSpacing: "0.02em" }}>PROVISION NEW BILLING</span>
                </button>
            </div>

        </div>

        {/* Top Metrics Row */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 24, marginBottom: 48 }}>
            <div className="premium-card" style={{ padding: 28, position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", top: -10, right: -10, fontSize: 80, opacity: 0.05 }}>📦</div>
                <div style={{ color: "var(--text-muted)", fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 16 }}>Inventory Reservoir</div>
                <div style={{ fontSize: 36, fontWeight: 900, color: "var(--text-main)" }}>{totalStock.toLocaleString()} <span style={{ fontSize: 14, fontWeight: 500, color: "var(--text-muted)" }}>Pieces</span></div>
                <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 12, color: "var(--success)", fontWeight: 700 }}>↑ 4.2%</span>
                    <span style={{ fontSize: 12, color: "var(--text-muted)" }}>vs yesterday</span>
                </div>
            </div>
            <div className="premium-card" style={{ padding: 28, position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", top: -10, right: -10, fontSize: 80, opacity: 0.05 }}>👥</div>
                <div style={{ color: "var(--text-muted)", fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 16 }}>Sales Force Active</div>
                <div style={{ fontSize: 36, fontWeight: 900, color: "var(--text-main)" }}>{dsList.length} <span style={{ fontSize: 14, fontWeight: 500, color: "var(--text-muted)" }}>Personnel</span></div>
                <div style={{ marginTop: 16, color: "var(--primary)", fontSize: 12, fontWeight: 700 }}>Network Fully Operational</div>
            </div>
            <div className="premium-card" style={{ padding: 28, position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", top: -10, right: -10, fontSize: 80, opacity: 0.05 }}>📑</div>
                <div style={{ color: "var(--text-muted)", fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 16 }}>Pending Consignments</div>
                <div style={{ fontSize: 36, fontWeight: 900, color: "var(--text-main)" }}>-- <span style={{ fontSize: 14, fontWeight: 500, color: "var(--text-muted)" }}>Requests</span></div>
                <div style={{ marginTop: 16, color: "var(--text-muted)", fontSize: 12 }}>Syncing with Cloud...</div>
            </div>
            <div className="premium-card" style={{ padding: 28, background: "linear-gradient(135deg, var(--primary), #6366f1)", color: "white", border: "none" }}>
                <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 16 }}>Cloud Infrastructure</div>
                <div style={{ fontSize: 28, fontWeight: 900, marginBottom: 4 }}>ENTERPRISE</div>
                <div style={{ fontSize: 13, color: "rgba(255,255,255,0.9)", fontWeight: 500 }}>Global Node V2.4</div>
                <div style={{ marginTop: 20, height: 4, background: "rgba(255,255,255,0.2)", borderRadius: 2, overflow: "hidden" }}>
                    <div style={{ width: "85%", height: "100%", background: "white" }}></div>
                </div>
            </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1.8fr 1fr", gap: 32 }}>
            {/* DS List Section */}
            <div className="premium-card" style={{ border: "1px solid var(--border)", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <div style={{ padding: "24px 28px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center", background: "var(--surface-hover)" }}>
                    <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>Field Registry Matrix</h3>
                    <div style={{ display: "flex", gap: 12 }}>
                        <div style={{ fontSize: 11, fontWeight: 800, color: "var(--text-muted)", background: "var(--surface)", padding: "6px 14px", borderRadius: 20, border: "1px solid var(--border)" }}>ACTIVE NODES: {dsList.length}</div>
                    </div>
                </div>
                <div style={{ maxHeight: 600, overflowY: "auto" }}>
                    <table className="enterprise-table">
                        <thead>
                            <tr>
                                <th style={{ paddingLeft: 28 }}>PERSONNEL SPECIFICATION</th>
                                <th>IDENTIFIER</th>
                                <th>PRIMARY LOGISTICS ROUTE</th>
                                <th style={{ textAlign: "right", paddingRight: 28 }}>OPERATIONAL ACCESS</th>
                            </tr>
                        </thead>
                        <tbody>
                            {dsList.map(ds => (
                                <tr key={ds.id}>
                                    <td style={{ paddingLeft: 28, fontWeight: 800, color: "var(--text-main)", fontSize: 15 }}>{ds.name}</td>
                                    <td><span style={{ fontSize: 12, fontWeight: 700, fontFamily: "monospace", color: "var(--primary)", background: "rgba(255, 107, 0, 0.05)", padding: "4px 8px", borderRadius: 4 }}>{ds.dsId}</span></td>
                                    <td style={{ color: "var(--text-muted)", fontWeight: 500 }}>{ds.route || "Ungated Territory"}</td>
                                    <td style={{ textAlign: "right", paddingRight: 28 }}>
                                        <button 
                                            onClick={() => navigate(`/distributor-bill-ds?dsId=${ds.dsId}`)}
                                            className="btn-secondary"
                                            style={{ 
                                                fontSize: 12, 
                                                fontWeight: 800, 
                                                padding: "8px 16px",
                                                borderRadius: 8,
                                                borderColor: "var(--primary)",
                                                color: "var(--primary)"
                                            }}
                                        >
                                            REQUISITION BILL
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {dsList.length === 0 && (
                      <div style={{ textAlign: "center", padding: 100, background: "var(--surface)" }}>
                        <div style={{ fontSize: 64, marginBottom: 24, filter: "grayscale(1)" }}>🛰️</div>
                        <div style={{ fontWeight: 800, color: "var(--text-main)", fontSize: 20 }}>No Network Nodes Detected</div>
                        <p style={{ fontSize: 15, color: "var(--text-muted)", maxWidth: 300, margin: "12px auto 0" }}>Contact system administration to provision field personnel to your territory.</p>
                      </div>
                    )}
                </div>
            </div>

            {/* Quick Actions & Audit Section */}
            <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
              <div className="premium-card" style={{ padding: 32 }}>
                  <h3 style={{ marginBottom: 24, fontSize: 18, fontWeight: 800 }}>Lifecycle Analytics</h3>
                  <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                      <div className="premium-card" style={{ padding: 20, background: "var(--surface-hover)", border: "1px solid var(--border)", boxShadow: "none" }}>
                          <div style={{ fontSize: 11, fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Ledger Audit Timestamp</div>
                          <div style={{ fontWeight: 800, fontSize: 18, color: "var(--text-main)" }}>{new Date().toLocaleDateString("en-IN", { day: 'numeric', month: 'long', year: 'numeric' })}</div>
                          <div style={{ fontSize: 12, color: "var(--success)", marginTop: 6, fontWeight: 500 }}>● Network Synchronization Active</div>
                      </div>
                      
                      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 16 }}>
                          <button 
                              onClick={() => navigate("/distributor-inventory-report")}
                              className="btn-primary"
                              style={{ width: "100%", padding: 16, background: "var(--text-main)", color: "white" }}
                          >
                              EXECUTIVE STOCK AUDIT
                          </button>
                          <button 
                              onClick={() => navigate("/ds-order-report")}
                              className="btn-secondary"
                              style={{ width: "100%", padding: 16, fontWeight: 800 }}
                          >
                              PERFORMANCE INTELLIGENCE
                          </button>
                      </div>
                  </div>
              </div>

              <div className="premium-card" style={{ padding: 32, borderLeft: "6px solid var(--warning)", background: "rgba(245, 158, 11, 0.02)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                      <span style={{ fontSize: 20 }}>🛡️</span>
                      <h4 style={{ margin: 0, color: "var(--warning)", fontSize: 15, fontWeight: 800 }}>Regulatory Compliance</h4>
                  </div>
                  <p style={{ fontSize: 13, color: "var(--text-muted)", margin: 0, lineHeight: 1.6 }}>
                      All financial transactions and generated invoices are digitally cryptographically hashed and archived for audit trails. Adherence to <strong>GST Protocol Section 14B</strong> is mandatory for all field operations.
                  </p>
              </div>

              <div className="premium-card" style={{ padding: 24, textAlign: "center", background: "var(--surface-hover)" }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 16 }}>Partner Support</div>
                  <button className="btn-secondary" style={{ width: "100%", borderRadius: 10 }}>RESOURCE CENTER</button>
              </div>
            </div>
        </div>
      </div>
    </Layout>
  );
}

export default DistributorDashboard;
